// Local AI fallback for the AIForm. This is deliberately deterministic:
// split pasted prose into likely event candidates, normalize relative
// dates into parseable dates, then delegate field extraction to the same
// Batch parser used by the manual batch flow.

import {
  addDays,
  dayOfWeek,
  instantFromZonedWallClock,
  isoDate,
  parseISODate,
  todayPlain,
} from '../../utils/dateUtils.js';
import { parseLine, type BatchParsedEvent } from '../../utils/batchParse.js';
import {
  DEFAULT_KIND_BEHAVIOR,
  inferKindFromText,
  resolveKindDefaults,
  type CalendarKindBehaviorMap,
} from '../../utils/kindBehavior.js';
import type { EventComposerSubmitDraft } from './types.js';

const CN_WEEKDAY: Readonly<Record<string, number>> = {
  日: 0,
  天: 0,
  一: 1,
  二: 2,
  三: 3,
  四: 4,
  五: 5,
  六: 6,
};

const EN_WEEKDAY: Readonly<Record<string, number>> = {
  sun: 0,
  mon: 1,
  tue: 2,
  wed: 3,
  thu: 4,
  fri: 5,
  sat: 6,
};

const HEADER_HINT_RE =
  /(?:事项|计划|安排|目前|如下|有[:：]?$|agenda|schedule|plan)/i;
const EXPLICIT_DATE_RE =
  /(?:\d{4}-\d{2}-\d{2}|(?:(?:\d{4})\s*年\s*)?\d{1,2}\s*月\s*\d{1,2}\s*[日号]?|\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{1,2}\b)/i;
const DATE_OR_TIME_CUE_RE =
  /(?:\d{4}-\d{2}-\d{2}|(?:(?:\d{4})\s*年\s*)?\d{1,2}\s*月\s*\d{1,2}\s*[日号]?|今天|明天|后天|(?:下|本|这|每)?(?:周|星期|礼拜)[一二三四五六日天]|\b(?:today|tomorrow|tonight)\b|\b(?:next|this|every)\s+(?:sun|mon|tue|wed|thu|fri|sat)[a-z]*\b|上午|下午|晚上|早上|凌晨|\d{1,2}:\d{2}|\d{1,2}\s*(?:am|pm|点|时)\b)/i;
const COMMA_EVENT_BOUNDARY_RE =
  /[,，、]\s*(?=(?:\d{4}-\d{2}-\d{2}|(?:(?:\d{4})\s*年\s*)?\d{1,2}\s*月\s*\d{1,2}\s*[日号]?|今天|明天|后天|(?:下|本|这)?(?:周|星期|礼拜)[一二三四五六日天]|\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\s+\d{1,2}\b))/gi;
const RECURRENCE_ONLY_RE =
  /^(?:每\s*(?:天|周|月|两周|双周|周[一二三四五六日天])|每周重复|重复|weekly|daily|monthly|biweekly|fortnightly|repeats?|repeat\s+(?:daily|weekly|monthly))$/i;
const COMPACT_SCHEDULE_ROW_RE =
  /^(?:(\d{4})\s*[-./]\s*)?(\d{1,2})\s*[-./]\s*(\d{1,2})(?:\s*[日号])?(?:\s*[（(]?(?:周|星期|礼拜)?[一二三四五六日天][)）]?)?(?:\s*[：:、.\-—–]\s*|\s+|(?=[^\d\s]))(.+)$/;
const CN_SCHEDULE_ROW_RE =
  /^(?:(?:(\d{4})\s*年\s*)?(\d{1,2})\s*月\s*)?(\d{1,2})\s*[日号]?(?:\s*[（(]?(?:周|星期|礼拜)?[一二三四五六日天][)）]?)?(?:\s*[：:、.\-—–]\s*|\s+|(?=[^\d\s]))(.+)$/;
const TIME_RANGE_FRAGMENT_RE =
  /((?:上午|早上|凌晨|下午|晚上|傍晚)?\s*\d{1,2}(?:[:：]\d{2}|点(?:\d{1,2}分?)?|时(?:\d{1,2}分?)?)\s*(?:[-–—~]|至|到)\s*(?:上午|早上|凌晨|下午|晚上|傍晚)?\s*\d{1,2}(?:[:：]\d{2}|点(?:\d{1,2}分?)?|时(?:\d{1,2}分?)?)(?:\s*(?:am|pm))?)/i;

interface ScheduleContext {
  readonly year: number;
  readonly month: number | null;
  readonly timeRange: string | null;
}

export interface LocalAIExtractOptions {
  readonly defaultGroupId: string | null;
  readonly kindKeys: readonly string[];
  readonly viewerTz: string | undefined;
  readonly kindBehavior?: CalendarKindBehaviorMap;
}

export function localAIExtract(
  text: string,
  options: LocalAIExtractOptions,
): readonly EventComposerSubmitDraft[] {
  const today = todayPlain(options.viewerTz);
  return localAIExtractFromSources(splitIntoEventCandidates(text, today), options, today);
}

export function localAIExtractStructuredSchedule(
  text: string,
  options: LocalAIExtractOptions,
): readonly EventComposerSubmitDraft[] {
  const today = todayPlain(options.viewerTz);
  return localAIExtractFromSources(expandCompactScheduleRows(text, today), options, today);
}

function localAIExtractFromSources(
  sources: readonly string[],
  options: LocalAIExtractOptions,
  today: ReturnType<typeof todayPlain>,
): readonly EventComposerSubmitDraft[] {
  const {
    defaultGroupId,
    kindKeys,
    viewerTz,
    kindBehavior = DEFAULT_KIND_BEHAVIOR,
  } = options;
  const fallbackKind = kindKeys.includes('publish')
    ? 'publish'
    : (kindKeys[0] ?? 'event');
  const out: EventComposerSubmitDraft[] = [];
  const seen = new Set<string>();

  for (const source of sources) {
    const inferredKind = inferKind(
      source,
      kindKeys,
      fallbackKind,
      kindBehavior,
    );
    const parsed = parseLine(normalizeRelativeDates(source, today), {
      defaultGroupId,
      kindKeys,
      kindBehavior,
      fallbackKind: inferredKind,
      defaultYear: today.year,
    });
    if (parsed.error) continue;

    const title = cleanTitle(parsed.title, inferredKind);
    const draft = parsedEventToDraft(
      {
        ...parsed,
        title,
        kind: inferKind(
          source + ' ' + parsed.title,
          kindKeys,
          parsed.kind,
          kindBehavior,
        ),
      },
      viewerTz,
      kindBehavior,
    );
    const dedupeKey = `${draft.startsAt}-${draft.kind}-${draft.title.slice(0, 30)}`;
    if (seen.has(dedupeKey)) continue;
    seen.add(dedupeKey);
    out.push(draft);
  }
  return out;
}

function splitIntoEventCandidates(
  text: string,
  today: ReturnType<typeof todayPlain>,
): readonly string[] {
  const scheduleRows = expandCompactScheduleRows(text, today);
  const rawParts: string[] = [];
  for (const rawLine of text.replace(/\r\n?/g, '\n').split(/\n+/)) {
    const line = stripListMarker(rawLine);
    if (line.length === 0 || line.startsWith('#')) continue;
    rawParts.push(
      ...line
        .replace(COMMA_EVENT_BOUNDARY_RE, '\n')
        .replace(/[;；。！？!?]\s*/g, '\n')
        .replace(
          /\s+(?:and then|also|plus|另外|还有|然后|并且|以及)\s+/gi,
          '\n',
        )
        .split(/\n+/)
        .map(stripListMarker)
        .filter((part) => part.length > 0),
    );
  }

  const out: string[] = [];
  let pendingPrefix: string | null = null;
  for (const part of rawParts) {
    if (RECURRENCE_ONLY_RE.test(part) && out.length > 0) {
      const previous = out[out.length - 1];
      if (previous !== undefined)
        out[out.length - 1] = `${previous} ${part}`.trim();
      continue;
    }
    const eventPart = stripInlineHeader(part);
    if (DATE_OR_TIME_CUE_RE.test(eventPart)) {
      const prefix =
        pendingPrefix !== null && !HEADER_HINT_RE.test(pendingPrefix)
          ? `${pendingPrefix} `
          : '';
      out.push(`${prefix}${eventPart}`.trim());
      pendingPrefix = null;
      continue;
    }
    if (out.length > 0) {
      const previous = out[out.length - 1];
      if (previous !== undefined)
        out[out.length - 1] = `${previous} ${part}`.trim();
    } else if (!HEADER_HINT_RE.test(part)) {
      pendingPrefix =
        pendingPrefix === null ? part : `${pendingPrefix} ${part}`;
    }
  }
  if (pendingPrefix !== null) out.push(pendingPrefix);

  const seen = new Set<string>();
  const candidates = scheduleRows.length >= 2 ? scheduleRows : [...scheduleRows, ...out];
  return candidates.filter((candidate) => {
    const compact = candidate.replace(/\s+/g, ' ').trim();
    if (compact.length === 0) return false;
    const key = compact.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function expandCompactScheduleRows(
  text: string,
  today: ReturnType<typeof todayPlain>,
): readonly string[] {
  const out: string[] = [];
  let context: ScheduleContext = { year: today.year, month: null, timeRange: null };
  for (const rawLine of text.replace(/\r\n?/g, '\n').split(/\n+/)) {
    const line = stripListMarker(rawLine);
    if (line.length === 0 || line.startsWith('#')) continue;

    const row = parseScheduleRow(line, context);
    if (row !== null) {
      out.push(row.source);
      continue;
    }
    context = readScheduleContext(line, context);
  }
  return out;
}

function parseScheduleRow(
  line: string,
  context: ScheduleContext,
): { readonly source: string } | null {
  return parseCompactScheduleRow(line, context) ?? parseChineseScheduleRow(line, context);
}

function parseCompactScheduleRow(
  line: string,
  context: ScheduleContext,
): { readonly source: string } | null {
  const match = COMPACT_SCHEDULE_ROW_RE.exec(line);
  if (match === null) return null;
  const year = match[1] === undefined ? context.year : Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const title = (match[4] ?? '').trim();
  return buildScheduleRow(year, month, day, title, context);
}

function parseChineseScheduleRow(
  line: string,
  context: ScheduleContext,
): { readonly source: string } | null {
  const match = CN_SCHEDULE_ROW_RE.exec(line);
  if (match === null) return null;
  const year = match[1] === undefined ? context.year : Number(match[1]);
  const month = match[2] === undefined ? context.month : Number(match[2]);
  if (month === null) return null;
  const day = Number(match[3]);
  const title = (match[4] ?? '').trim();
  return buildScheduleRow(year, month, day, title, context);
}

function buildScheduleRow(
  year: number,
  month: number,
  day: number,
  title: string,
  context: ScheduleContext,
): { readonly source: string } | null {
  if (!isValidMonthDay(month, day) || title.length === 0) return null;
  const time = context.timeRange === null ? '' : ` ${context.timeRange}`;
  return {
    source: `${year}年${month}月${day}日${time} ${title}`.trim(),
  };
}

function readScheduleContext(
  line: string,
  previous: ScheduleContext,
): ScheduleContext {
  let year = previous.year;
  let month = previous.month;
  let timeRange = previous.timeRange;

  const yearMonthMatch = /(\d{4})\s*年(?:\s*(\d{1,2})\s*月)?/.exec(line);
  if (yearMonthMatch !== null) {
    const nextYear = Number(yearMonthMatch[1]);
    const nextMonth = yearMonthMatch[2] === undefined ? null : Number(yearMonthMatch[2]);
    if (Number.isInteger(nextYear)) year = nextYear;
    if (nextMonth !== null && nextMonth >= 1 && nextMonth <= 12) month = nextMonth;
  }

  if (month === previous.month) {
    const monthMatch =
      /(\d{1,2})\s*月\s*(?:开始|起|课程|上课|安排|如下|内容)/.exec(line);
    if (monthMatch !== null) {
      const nextMonth = Number(monthMatch[1]);
      if (nextMonth >= 1 && nextMonth <= 12) month = nextMonth;
    }
  }

  const timeLine = normalizeChineseClockText(line);
  const timeMatch = TIME_RANGE_FRAGMENT_RE.exec(timeLine);
  if (timeMatch !== null)
    timeRange = normalizeChineseClockText(timeMatch[1] ?? '').replace(/\s+/g, '') || timeRange;
  return { year, month, timeRange };
}

function isValidMonthDay(month: number, day: number): boolean {
  return month >= 1 && month <= 12 && day >= 1 && day <= 31;
}

function normalizeChineseClockText(source: string): string {
  return source
    .replace(/(\d{1,2})：(\d{2})/g, '$1:$2')
    .replace(
      /(\d{1,2})\s*(?:点|时)\s*(?:(\d{1,2})\s*分?)?/g,
      (_match: string, hour: string, minute: string | undefined): string =>
        `${hour}:${String(minute === undefined ? 0 : Number(minute)).padStart(2, '0')}`,
    );
}

function stripListMarker(line: string): string {
  return line
    .trim()
    .replace(/^[-*•]\s+/, '')
    .replace(/^\d{1,3}[.)、]\s+/, '')
    .replace(/^[一二三四五六七八九十]{1,3}[.)、]\s+/, '')
    .trim();
}

function stripInlineHeader(candidate: string): string {
  return candidate
    .replace(/^.*?(?:事项|计划|安排|目前有|如下)[:：]\s*/, '')
    .trim();
}

function inferKind(
  source: string,
  kindKeys: readonly string[],
  fallback: string,
  kindBehavior: CalendarKindBehaviorMap,
): string {
  return inferKindFromText(source, kindKeys, kindBehavior, fallback);
}

function normalizeRelativeDates(
  source: string,
  today: ReturnType<typeof todayPlain>,
): string {
  if (EXPLICIT_DATE_RE.test(source)) return source;
  let out = source
    .replace(/今天|today\b/gi, isoDate(today))
    .replace(/明天|tomorrow\b/gi, isoDate(addDays(today, 1)))
    .replace(/后天/gi, isoDate(addDays(today, 2)));

  out = out.replace(
    /(下|本|这|每)?(?:周|星期|礼拜)([一二三四五六日天])/g,
    (match: string, prefix: string | undefined, rawWeekday: string): string => {
      const target = CN_WEEKDAY[rawWeekday];
      if (target === undefined) return match;
      const date = dateForWeekday(today, target, prefix ?? '');
      return prefix === '每' ? `${isoDate(date)} 每周` : isoDate(date);
    },
  );

  out = out.replace(
    /\b(next|this|every)?\s*(sun|mon|tue|wed|thu|fri|sat)[a-z]*\b/gi,
    (match: string, prefix: string | undefined, rawWeekday: string): string => {
      const target = EN_WEEKDAY[rawWeekday.toLowerCase().slice(0, 3)];
      if (target === undefined) return match;
      const normalizedPrefix = prefix?.toLowerCase() ?? '';
      const date = dateForWeekday(today, target, normalizedPrefix);
      return normalizedPrefix === 'every'
        ? `${isoDate(date)} weekly`
        : isoDate(date);
    },
  );
  return out;
}

function dateForWeekday(
  today: ReturnType<typeof todayPlain>,
  targetWeekday: number,
  prefix: string,
): ReturnType<typeof todayPlain> {
  const current = dayOfWeek(today);
  if (prefix === '下' || prefix === 'next') {
    return addDays(today, ((targetWeekday - current + 7) % 7) + 7);
  }
  let offset = (targetWeekday - current + 7) % 7;
  if (prefix !== '本' && prefix !== '这' && prefix !== 'this') {
    offset = offset === 0 ? 7 : offset;
  }
  return addDays(today, offset);
}

function cleanTitle(title: string, fallback: string): string {
  const cleaned = title
    .replace(
      /(?:^|[，,、])\s*(?:每周重复|重复|每周|weekly|daily|monthly)\s*$/i,
      '',
    )
    .replace(/[，,]\s*至.*$/i, '')
    .replace(/^(?:从|于|在)\s*/, '')
    .replace(/从.*改到/g, '')
    .replace(/\s*(?:起|开始)\s*/g, ' ')
    .replace(/\s+发$/g, '')
    .replace(/[：:，,、]\s*$/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  if (cleaned.length === 0 || cleaned === 'Untitled event') return fallback;
  return cleaned.length > 80 ? cleaned.slice(0, 80).trim() + '…' : cleaned;
}

function parsedEventToDraft(
  parsed: BatchParsedEvent,
  viewerTz: string | undefined,
  kindBehavior: CalendarKindBehaviorMap,
): EventComposerSubmitDraft {
  const pd = parseISODate(parsed.date);
  const defaults = resolveKindDefaults(kindBehavior, parsed.kind);
  let startsAt: string;
  let endsAt: string | null = null;

  if (parsed.allDay) {
    startsAt = parsed.date;
  } else {
    const [hh, mm] = parsed.time.split(':').map(Number);
    const startMs = instantFromZonedWallClock(
      pd.year,
      pd.month,
      pd.day,
      hh ?? 9,
      mm ?? 0,
      viewerTz,
    );
    startsAt = new Date(startMs).toISOString();
    if (parsed.endTime !== null) {
      const [eh, em] = parsed.endTime.split(':').map(Number);
      endsAt = new Date(
        instantFromZonedWallClock(
          pd.year,
          pd.month,
          pd.day,
          eh ?? 9,
          em ?? 0,
          viewerTz,
        ),
      ).toISOString();
    } else if (parsed.durationMinutes !== null && parsed.durationMinutes > 0) {
      endsAt = new Date(
        startMs + parsed.durationMinutes * 60_000,
      ).toISOString();
    } else if (defaults.durationMinutes > 0) {
      endsAt = new Date(
        startMs + defaults.durationMinutes * 60_000,
      ).toISOString();
    }
  }

  return {
    title: parsed.title,
    kind: parsed.kind,
    startsAt,
    endsAt,
    allDay: parsed.allDay,
    groupId: parsed.groupId,
    crossPostedTo: [],
    meta: '',
    recurring: parsed.recurrence !== null,
    recurrence: parsed.recurrence,
  };
}
